<script setup>
import { onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import ModalBase from "@/components/modal/ModalBase.vue";
import ModalTabContent from "@/components/modal/ModalTabContent.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import { taskStore } from "@/_store/taskStore";
import { dateUtils, errorUtils } from "@/utils";
import { formatCourier, formatHandlingType, formatVolume, formatWeight } from "@/utils/formatters";

const props = defineProps({
  modalId: { type: String, required: true },
  taskId: {
    type: String,
    required: true,
    default() {
      return null;
    },
  },
});

const emit = defineEmits(["close"]);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const tasksStore = taskStore();
const snackbar = ref(null);
const currentTab = ref("manipulacion");
const detailTabs = [
  { label: "Manipulación", value: "manipulacion" },
  { label: "Pesos y medidas", value: "pesos" },
  { label: "Reparto", value: "reparto" },
];

const tabContentHeight = 110;

const emptyDetail = Object.freeze({
  _id: "",
  barcode: "",
  shipment_external_id: "",
  stop_id: "",
  created_at: "",
  account_id: "",
  stop_number: "",
  shipment_reference: "",
  weight: "",
  volumetric_weight: "",
  measurements: "",
  handling_type: "",
  content_description: "",
  courier: "",
  delivery_info: {
    status: "",
    status_description: "",
    date: null,
  },
  updated_at: "",
});

const taskDetail = ref({ ...emptyDetail });

function getTaskStatusTooltip(taskItem) {
  const status = String(taskItem?.delivery_info?.status || "").toUpperCase();
  const description = String(taskItem?.delivery_info?.status_description || "").trim();

  if (status === "INCIDENCIA" && description) {
    return `Incidencia: ${description}`;
  }

  return "";
}

function formatDeliveryDate(value) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  return dateUtils.formatUtcDate(text, "DD/MM/YYYY HH:mm");
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo cargar el detalle del bulto",
    type: "danger",
    color: "white",
  };
}

function loadTaskDetail() {
  closeSnackbar();
  tasksStore.getTaskById(props.taskId);
}

function handleHide() {
  currentTab.value = "manipulacion";
  emit("close");
}

watch(
  () => tasksStore.task,
  (value) => {
    if (!value) return;
    taskDetail.value = {
      ...emptyDetail,
      ...(value || {}),
    };
  }
);

watch(
  () => tasksStore.error,
  (value) => {
    if (!value) return;
    showError(
      errorUtils.getErrorMessageHtml(
        value,
        "No se pudo cargar el detalle del bulto"
      )
    );
    taskDetail.value = { ...emptyDetail };
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadTaskDetail();
});
</script>

<template>
  <modal-base :id="modalId" width="38rem" @hide="handleHide">
    <template #header>
      <h6 class="mb-0">Detalle</h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Código de barras</label>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ taskDetail.barcode ?? "" }}</span>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Referencia</label>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">
              {{ taskDetail.shipment_reference ?? "" }}
            </span>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Número parada</label>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ taskDetail.stop_number ?? "" }}</span>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Transportista</label>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ formatCourier(taskDetail.courier) || "-" }}</span>
          </div>
        </div>

        <!-- <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Id externo</label>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">
              {{ taskDetail.shipment_external_id ?? "" }}
            </span>
          </div>
        </div> -->

        <tabs-bootstrap
          v-model="currentTab"
          :tabs="detailTabs"
          size="sm"
          refined
          style-nav="nav-underline"
        />

        <modal-tab-content :height="`${tabContentHeight}px`">
        <div v-if="currentTab === 'manipulacion'">
          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Manipulación</label>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatHandlingType(taskDetail.handling_type) }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Naturaleza</label>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ taskDetail.content_description || "-" }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="currentTab === 'pesos'">
          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Peso</label>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatWeight(taskDetail.weight) }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Peso volumétrico</label>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatVolume(taskDetail.volumetric_weight) }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Medidas</label>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ taskDetail.measurements ?? "" }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="currentTab === 'reparto'">
          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Estado</label>
            </div>
            <div class="col modal-form-value modal-form-value--auto">
              <argon-status
                entity="task"
                :status="taskDetail.delivery_info?.status || 'PENDIENTE'"
                :tooltip-external-text="getTaskStatusTooltip(taskDetail)"
                width="100px"
              />
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label class="col-form-label">Fecha</label>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">
                {{ formatDeliveryDate(taskDetail.delivery_info?.date) }}
              </span>
            </div>
          </div>
        </div>
        </modal-tab-content>
      </div>
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
